<template>
    <AppHead title="Login" />
    <div class="w-full max-w-sm mx-auto" :key="forceUpdate">
        <h1 class="text-3xl text-gray-800 text-center font-bold mb-8">
            Sign in
        </h1>

        <div class="mb-4" v-if="page.props?.flash?.success">
            <UiAlert
                title="Account Closed"
                :description="page.props.flash.success"
                :showClose="false"
                :bgColor="'green'"
                v-if="page.props?.flash?.closed_account"
            />
            <UiAlert
                title="Invitation Success!"
                :description="page.props.flash.success"
                :showClose="false"
                :bgColor="'green'"
                v-else
            />
        </div>

        <div class="relative" v-if="!showTwoFactor && !show2FAModal">
            <Sso :lastLoginMethod="lastLoginMethod" />
            <div class="relative">
                <OrActionBanner action="sign in" />
                <LastUsedPill v-if="lastLoginMethod === 'email'" />
            </div>
            <form
                method="post"
                action="/login"
                class="hs-form-login"
                data-hs-cf-bound="true"
                @submit.prevent="loginStore.login()"
                @keyup.enter.prevent="loginStore.login()"
            >
                <div class="space-y-5 mb-6">
                    <div class="relative">
                        <UiInput
                            type="email"
                            id="email"
                            name="email"
                            label="Email Address"
                            v-model:inputValue="loginCreds.email"
                            :errors="formErrors"
                            @blur="checkPasskeyAvailability(true)"
                        />
                        <!-- Manual advance button - caret icon to trigger passkey check (always visible as fallback) -->
                        <button
                            v-if="!hasCheckedPasskeys"
                            @click.prevent="handleManualCheck"
                            type="button"
                            class="absolute right-4 top-[34px] text-slate-600 hover:text-slate-800 transition-colors"
                            title="Check for passkey"
                        >
                            <Icon icon="chevron-right" class="w-5 h-5" />
                        </button>
                    </div>

                    <!-- Show password field based on computed logic -->
                    <div v-if="shouldShowPasswordField">
                        <UiInput
                            type="password"
                            id="password"
                            name="password"
                            label="Password"
                            v-model:inputValue="loginCreds.password"
                            :errors="formErrors"
                            :options="{ toggleHidden: true }"
                        />
                        <div class="flex justify-between items-center pt-1">
                            <!-- Show "Use Passkey instead" link if user has passkeys and is using password -->
                            <button
                                v-if="hasPasskeys && showPasswordField"
                                type="button"
                                @click="showPasswordField = false"
                                class="text-sm text-slate-600 hover:text-slate-800 underline"
                            >
                                Use Passkey instead
                            </button>
                            <span v-else></span>

                            <a class="link text-sm" href="/forgot-password">
                                Forgot Password?
                            </a>
                        </div>
                    </div>

                    <!-- Show "Use password instead" link if user has passkeys but password field is hidden -->
                    <div
                        v-if="hasPasskeys && !showPasswordField"
                        class="text-center"
                    >
                        <button
                            type="button"
                            @click="showPasswordField = true"
                            class="text-sm text-slate-600 hover:text-slate-800 underline"
                        >
                            Use password instead
                        </button>
                    </div>
                </div>

                <Errors :errors="errors" class="mb-4" />

                <!-- Show Passkey button if user has passkeys AND hasn't chosen to use password -->
                <PasskeyLoginButton
                    v-if="hasPasskeys && !showPasswordField"
                    :userHasPasskeys="hasPasskeys"
                />

                <!-- Only show Sign In button if password field is visible -->
                <ActionButton
                    v-if="shouldShowPasswordField"
                    @clicked="loginStore.login()"
                    label="Sign in"
                    :loading="loading"
                    :useEnter="true"
                    classes="bg-slate-800 hover:bg-slate-600 text-sm px-4 py-2.5 rounded shadow transition-colors text-white font-medium w-full"
                />

                <div class="pt-6 text-center text-slate-800 text-sm pb-safe">
                    Don't yet have an account?
                    <Link
                        class="font-medium text-orange hover:text-orange-light transition-colors"
                        href="register"
                    >
                        Sign up
                    </Link>
                </div>
            </form>
        </div>
        <TotpTwoFactor v-if="showTwoFactor" />
        <TwoFactor
            :show2FAModal="show2FAModal"
            @close="loginStore.close2FAModal()"
            :intended="intended"
            :user="user"
            :phone="phone"
            mode="login"
        />
    </div>
</template>
<script setup>
import { usePage } from '@inertiajs/vue3';
import { storeToRefs } from 'pinia';
import { useLoginStore } from '@/store/login';
import { onMounted, watch, computed, ref, nextTick } from 'vue';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import UiAlert from '@/components/ui/alert/ui-alert.vue';
import Sso from '@/components/auth/sso.vue';
import PasskeyLoginButton from '@/components/auth/passkey-login-button.vue';
import ActionButton from '@/components/action-button.vue';
import Errors from '@/components/errors.vue';
import TotpTwoFactor from '@/components/auth/login-steps/totp-two-factor.vue';
import TwoFactor from '@/components/auth/two-factor.vue';
import OrActionBanner from '@/components/auth/or-action-banner.vue';
import LastUsedPill from '@/components/auth/last-used-pill.vue';
import Icon from '@/components/ui/icon/icon.vue';
import { Link } from '@inertiajs/vue3';
import AppHead from '@/Partials/AppHead.vue';
import Cookies from 'js-cookie';
import { removeItem } from '@/utils/localStorage';

const loginStore = useLoginStore();
const {
    loginCreds,
    errors,
    formErrors,
    loading,
    show2FAModal,
    showTwoFactor,
    user,
    phone,
    intended,
} = storeToRefs(loginStore);

const lastLoginMethod = Cookies.get('last_login_method') || null;

// Create a computed property for hasPasskeys to ensure reactivity
const hasPasskeys = computed(() => loginStore.hasPasskeys);

// Track whether we've checked for passkeys yet
const hasCheckedPasskeys = ref(false);

// Toggle to show password field when user wants to use password instead of passkey
const showPasswordField = ref(false);

// Computed: Show password field if we've checked and user has NO passkeys, or if user manually chose password
// Note: This reactive property automatically hides the password field when passkeys are detected,
// eliminating the need to manually clear autofilled passwords (which created a janky UX)
const shouldShowPasswordField = computed(() => {
    // If user manually toggled to show password, always show it
    if (showPasswordField.value) {
        return true;
    }

    // If we've checked for passkeys and user doesn't have any, show password field
    if (hasCheckedPasskeys.value && !hasPasskeys.value) {
        return true;
    }

    // Otherwise, hide password field (default state or user has passkeys)
    return false;
});

// Force re-render trigger for autofill detection
const forceUpdate = ref(0);

const page = usePage();

removeItem('_token');

const storeSessions = () => {
    // Get UTM parameters from URL instead of localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const utm_campaign = urlParams.get('utm_campaign');
    const utm_content = urlParams.get('utm_content');
    const utm_medium = urlParams.get('utm_medium');
    const utm_source = urlParams.get('utm_source');
    const utm_term = urlParams.get('utm_term');

    loginStore.setUtmParams({
        utm_campaign,
        utm_content,
        utm_medium,
        utm_source,
        utm_term,
    });
};

// Debounce timer for passkey availability checks
let passkeyCheckTimeout = null;
const lastCheckedEmail = ref(null);

// Helper function to check if email looks valid (has @ and domain with .)
const isValidEmailFormat = (email) => {
    if (!email || typeof email !== 'string') return false;

    // Basic email format: something@something.something
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

const checkPasskeyAvailability = (immediate = false) => {
    const email = loginCreds.value.email;

    // Skip if no email, invalid format, or already checked this email
    if (
        !email ||
        !isValidEmailFormat(email) ||
        email === lastCheckedEmail.value
    ) {
        return;
    }

    // Clear existing timeout
    if (passkeyCheckTimeout) {
        clearTimeout(passkeyCheckTimeout);
    }

    // Function to perform the actual check
    const performCheck = async () => {
        lastCheckedEmail.value = email;
        await loginStore.checkPasskeyAvailability(email);
        // Mark that we've checked for passkeys
        hasCheckedPasskeys.value = true;
    };

    // If immediate (from blur event) or email looks complete, check right away
    if (immediate) {
        performCheck();
    } else {
        // Otherwise debounce by 300ms for typing
        passkeyCheckTimeout = setTimeout(performCheck, 300);
    }
};

// Handle manual check from chevron button - only proceed if email is valid
const handleManualCheck = () => {
    const email = loginCreds.value.email;

    // Only trigger if email is valid
    if (email && isValidEmailFormat(email)) {
        checkPasskeyAvailability(true);
    }
};

// Watch for email changes and check passkey availability
watch(
    () => loginCreds.value.email,
    (newEmail, oldEmail) => {
        // Only check if email looks valid and has changed
        if (newEmail && isValidEmailFormat(newEmail) && newEmail !== oldEmail) {
            checkPasskeyAvailability();
        }
    }
);

// Watch hasPasskeys value changes (for debugging if needed)
// watch(() => hasPasskeys.value, (newVal) => {
//     // Passkey availability changed
// });

// Check for autofill - waits for DOM element to exist
const checkAutofillEarly = async () => {
    const emailField = document.getElementById('email');
    if (!emailField) {
        return;
    }

    // Check if field is autofilled using CSS pseudo-class. Browsers that
    // don't know a selector throw instead of returning false (Chrome 109 and
    // :autofill).
    const matchesSelector = (selector) => {
        try {
            return emailField.matches(selector);
        } catch {
            return false;
        }
    };
    const isAutofilled =
        matchesSelector(':-webkit-autofill') || matchesSelector(':autofill');

    // If autofilled but value is empty, trigger a focus/blur to force browser to populate value
    if (isAutofilled && !emailField.value) {
        emailField.focus();
        emailField.blur();
        // Wait a moment for browser to update the value
        await new Promise((resolve) => setTimeout(resolve, 50));
    }

    if (emailField.value && emailField.value.includes('@')) {
        // Force update the reactive value
        const email = emailField.value;
        loginCreds.value.email = email;

        // Force Vue to re-render by incrementing forceUpdate
        forceUpdate.value++;
        await nextTick();

        // Check for passkeys
        checkPasskeyAvailability();
    }
};

// Start checking after a delay to ensure DOM is ready
// Reduced to 3 checks at strategic intervals
setTimeout(checkAutofillEarly, 100); // Quick check
setTimeout(checkAutofillEarly, 500); // Medium delay for slower autofill
setTimeout(checkAutofillEarly, 1000); // Final check for very slow autofill

// Add a one-time listener for ANY user interaction to check autofill
// This works around Chrome's security feature that prevents reading autofill values
const checkOnInteraction = async () => {
    const emailField = document.getElementById('email');
    if (emailField?.value && emailField.value.includes('@')) {
        loginCreds.value.email = emailField.value;
        forceUpdate.value++;
        await nextTick();
        checkPasskeyAvailability();
    }
    // Remove listeners after first check
    document.removeEventListener('click', checkOnInteraction);
    document.removeEventListener('keydown', checkOnInteraction);
    document.removeEventListener('touchstart', checkOnInteraction);
};

// Listen for first interaction
document.addEventListener('click', checkOnInteraction, { once: false });
document.addEventListener('keydown', checkOnInteraction, { once: false });
document.addEventListener('touchstart', checkOnInteraction, { once: false });

onMounted(() => {
    // SSO 2FA hand-off (SUPPORT-663 / F-008): the OAuth callback authenticated
    // the session but Check2FA gates every protected route until the SMS code
    // is verified. The /verify-2fa screen renders this page with `ssoTwoFactor`
    // set, so open the SMS modal immediately.
    if (page.props.ssoTwoFactor) {
        loginStore.phone = page.props.phone;
        loginStore.intended = page.props.intended || '/dashboard';
        loginStore.mode = 'login';
        loginStore.show2FAModal = true;
        return;
    }

    // Google/Apple sign-in for an account with an authenticator app: the
    // session isn't signed in yet, and the authenticator step signs it in
    // once the code checks out (/verify-authenticator).
    if (page.props.ssoAuthenticatorCode) {
        loginStore.intended = page.props.intended || '/dashboard';
        loginStore.showTwoFactor = true;
        return;
    }

    const emailField = document.getElementById('email');
    if (emailField) {
        emailField.focus();

        // Check autofill immediately after focus (focus can trigger value population)
        setTimeout(async () => {
            if (emailField.value && emailField.value.includes('@')) {
                loginCreds.value.email = emailField.value;
                forceUpdate.value++;
                await nextTick();
                checkPasskeyAvailability();
            }
        }, 100);

        // Add native blur event listener as backup
        emailField.addEventListener('blur', () => {
            checkPasskeyAvailability();
        });

        // Detect browser autofill and check for passkeys
        const checkAutofill = async () => {
            if (emailField.value && emailField.value.includes('@')) {
                // Force update the reactive value
                const email = emailField.value;
                loginCreds.value.email = email;

                // Force Vue to re-render
                forceUpdate.value++;
                await nextTick();

                // Check for passkeys
                checkPasskeyAvailability();
            }
        };

        // Check at strategic intervals after mount
        setTimeout(checkAutofill, 100);
        setTimeout(checkAutofill, 500);

        // Also listen for the animationstart event which fires when autofill happens
        emailField.addEventListener('animationstart', (e) => {
            if (e.animationName === 'onAutoFillStart') {
                setTimeout(checkAutofill, 50);
            }
        });

        // Listen for input event (fires when autofill completes in some browsers)
        emailField.addEventListener('input', () => {
            if (emailField.value && emailField.value.includes('@')) {
                checkPasskeyAvailability();
            }
        });
    }
    storeSessions();
});
</script>

<style scoped>
/* Detect autofill using animation trick */
@keyframes onAutoFillStart {
    from {
        opacity: 0.99;
    }
    to {
        opacity: 1;
    }
}

input:-webkit-autofill {
    animation-name: onAutoFillStart;
    animation-duration: 0.001s;
}
</style>
